import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/studentAuthContext";

import StepCourseLocation from "../../components/enrollForm/StepCourseLocation";
import StepDeliveryMethod from "../../components/enrollForm/StepDeliveryMethod";
import StepTutorSelect from "../../components/enrollForm/StepTutorSelect";
import StepPayment from "../../components/enrollForm/StepPayment";

// ------------------- API & Config -------------------
const BASE_URL = process.env.REACT_APP_BASE_URL;
const PAYSTACK_PUBLIC_KEY =
  process.env.REACT_APP_PAYSTACK_PUBLIC_KEY ||
  "pk_live_e2c76d414fdb6b5819fb2d8489e22872a1e64d9d";

// ------------------- Language Translations -------------------
const translations = {
  en: {
    steps: ["Course", "Learning Mode", "Select Tutor", "Payment"],
    backClasses: "Back to Classes",
    continue: "Continue",
    back: "Back",
    enrollmentSuccess: "Enrollment & payment verified successfully!",
    dataLoadError: "Error loading data. Please try again.",
  },
  ha: {
    steps: ["Kwas", "Hanyar Koyo", "Zaɓi Malami", "Biya"],
    backClasses: "Komawa Koyarwa",
    continue: "Ci gaba",
    back: "Baya",
    enrollmentSuccess: "Rajista & biyan kuɗi an tabbatar!",
    dataLoadError: "Kuskure wajen ɗaukar bayanai. Da fatan a gwada sakewa.",
  },
  ig: {
    steps: ["Kọs", "Ụzọ Ịmụ", "Họrọ Onye Nkuzi", "Ịkwụ Ụgwọ"],
    backClasses: "Laghachị na Klasi",
    continue: "Gaa n’ihu",
    back: "Laghachi",
    enrollmentSuccess: "Ndebanye & ịkwụ ụgwọ rụzuru nke ọma!",
    dataLoadError: "Njehie n’ịbubata data. Biko gbalịa ọzọ.",
  },
  yo: {
    steps: ["Ẹkọ", "Ọna Ẹkọ", "Yan Olukọ", "Sisanwo"],
    backClasses: "Pada si Awọn Kilasi",
    continue: "Tesiwaju",
    back: "Pada",
    enrollmentSuccess: "Iforukọsilẹ & sisanwo ti fọwọsi!",
    dataLoadError: "Aṣiṣe nigba gbigba data. Jọwọ gbiyanju lẹẹkansi.",
  },
};

// ------------------- Main Component -------------------
export default function EnrollForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { student } = useAuth();

  const initialStep = location.state?.step ?? 0;
  const [activeStep, setActiveStep] = useState(initialStep);
  const processedEnrollVerifyRef = useRef(new Set());
  const [lang, setLang] = useState("en");
  const t = translations[lang];

  // ------------------- API Data -------------------
  const [courses, setCourses] = useState([]);
  const [tutorCourses, setTutorCourses] = useState([]);
  const [offices, setOffices] = useState([]);

  const [formData, setFormData] = useState({
    course: location.state?.courseId || "",
    location: null,
    deliveryMethod: "online",
    officeId: null,
    selectedTutor: location.state?.tutorId || null,
    paymentPlan: location.state?.paymentPlan || "installment",
    termsAccepted: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const handleSnackbarClose = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  // ------------------- Fetch Initial Data -------------------
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [courseRes, tutorCourseRes, officesRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/view/courses`),
          axios.get(`${BASE_URL}/api/view/all-tutor-courses`),
          axios.get(`${BASE_URL}/api/view/offices`),
        ]);
        setCourses(courseRes.data || []);
        setTutorCourses(tutorCourseRes.data || []);
        setOffices(officesRes.data || []);
      } catch (err) {
        console.error("Error loading data:", err);
        setSnackbar({
          open: true,
          message: t.dataLoadError,
          severity: "error",
        });
      }
    };

    if (BASE_URL) fetchInitial();
  }, [lang]);

  useEffect(() => {
    const verifyRedirectPayment = async () => {
      const params = new URLSearchParams(location.search);
      const paymentType = params.get("payment");
      const reference = params.get("reference") || params.get("trxref");

      if (paymentType !== "course-enrollment" || !reference || !student?.id) {
        return;
      }

      if (processedEnrollVerifyRef.current.has(reference)) {
        return;
      }
      processedEnrollVerifyRef.current.add(reference);

      try {
        const verifyRes = await axios.post(`${BASE_URL}/api/students/verify-course-enrollment`, {
          reference,
          student_id: student.id,
          email: student.email,
          name: student.name,
        });

        if (verifyRes.data?.success) {
          handlePaymentSuccess();
        } else {
          setSnackbar({
            open: true,
            message: verifyRes.data?.message || "Payment verification failed.",
            severity: "error",
          });
        }
      } catch (err) {
        console.error("Enrollment verification error:", err);
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || "Payment verification failed.",
          severity: "error",
        });
      } finally {
        window.history.replaceState({}, document.title, "/enroll");
      }
    };

    verifyRedirectPayment();
  }, [location.search, student?.id]);

  // ------------------- Pre-fill tutor if coming from TutorDetails -------------------
  useEffect(() => {
    if (location.state?.tutorId && tutorCourses.length > 0) {
      const tutorCourse = tutorCourses.find(
        (t) => t.tutor_course_id === location.state.tutorId
      );
      if (tutorCourse) {
        const courseObj = courses.find(
          (c) =>
            c.name?.toLowerCase() === tutorCourse.course_name?.toLowerCase()
        );
        setFormData((prev) => ({
          ...prev,
          course: courseObj?.id || "",
          selectedTutor: tutorCourse.tutor_course_id,
          deliveryMethod: "online",
          paymentPlan: "installment",
        }));
        setActiveStep(3); // jump directly to payment step
      }
    }
  }, [location.state, tutorCourses, courses]);

  // ------------------- Step Navigation -------------------
  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // ------------------- Selected Course & Tutor -------------------
  const selectedCourseObject = useMemo(
    () => courses.find((c) => c.id === formData.course),
    [courses, formData.course]
  );

  const filteredTutors = useMemo(() => {
    if (!selectedCourseObject) return [];
    return tutorCourses.filter(
      (t) =>
        selectedCourseObject?.name?.toLowerCase() === t.course_name?.toLowerCase()
    );
  }, [tutorCourses, selectedCourseObject]);

  const selectedTutorCourse = useMemo(
    () => tutorCourses.find((t) => t.tutor_course_id === formData.selectedTutor),
    [tutorCourses, formData.selectedTutor]
  );

  const selectedCourse = useMemo(() => {
    if (!selectedTutorCourse) return null;
    return { name: selectedTutorCourse.course_name, price: Number(selectedTutorCourse.price) || 0 };
  }, [selectedTutorCourse]);

  const getAdjustedPrice = (price) => Number(price || 0);
  const fullPaymentAmount = selectedCourse ? getAdjustedPrice(selectedCourse.price) : 0;
  const installmentAmount = selectedCourse
    ? fullPaymentAmount <= 80000
      ? Math.floor(fullPaymentAmount / 2)
      : Math.max(80000, Math.floor(fullPaymentAmount * 0.3))
    : 80000;

  // ------------------- Payment Success -------------------
  const handlePaymentSuccess = () => {
    setSnackbar({
      open: true,
      message: t.enrollmentSuccess,
      severity: "success",
    });
    setTimeout(() => navigate("/dashboard"), 2000);
  };

  return (
    <Box sx={{ maxWidth: "800px", mx: "auto", p: 3, mt: 12 }}>
      {/* Language Selector */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Select value={lang} onChange={(e) => setLang(e.target.value)}>
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="ha">Hausa</MenuItem>
          <MenuItem value="ig">Igbo</MenuItem>
          <MenuItem value="yo">Yoruba</MenuItem>
        </Select>
      </Box>

      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        {t.backClasses}
      </Button>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          "& .MuiStepLabel-root .MuiStepLabel-label": {
            fontWeight: "bold",
            color: "rgba(0,0,0,0.8)",
          },
        }}
      >
        {t.steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card
        elevation={3}
        sx={{
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(255,255,255,0.15)",
          borderRadius: 3,
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
        }}
      >
        <CardContent>
          {activeStep === 0 && (
            <StepCourseLocation
              courses={courses}
              formData={formData}
              handleChange={(e) => {
                const { name, value } = e.target;
                setFormData((prev) => ({
                  ...prev,
                  course: name === "course" ? value : prev.course,
                  selectedTutor: name === "course" ? null : prev.selectedTutor,
                }));
              }}
            />
          )}

          {activeStep === 1 && (
            <StepDeliveryMethod
              formData={formData}
              setFormData={setFormData}
              offices={offices}
            />
          )}

          {activeStep === 2 && (
            <StepTutorSelect
              tutors={filteredTutors}
              allTutors={tutorCourses}
              formData={formData}
              onSelectTutor={(id) => setFormData((p) => ({ ...p, selectedTutor: id }))}
            />
          )}

          {activeStep === 3 && selectedTutorCourse && (
            <StepPayment
              BASE_URL={BASE_URL}
              PAYSTACK_PUBLIC_KEY={PAYSTACK_PUBLIC_KEY}
              student={student}
              formData={formData}
              setFormData={setFormData}
              selectedCourse={selectedCourse}
              selectedTutorCourse={selectedTutorCourse}
              installmentAmount={installmentAmount}
              fullPaymentAmount={fullPaymentAmount}
              setSnackbar={setSnackbar}
              onSuccess={handlePaymentSuccess}
            />
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          {t.back}
        </Button>

        {activeStep < t.steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !formData.course) ||
              (activeStep === 1 && !formData.deliveryMethod) ||
              (activeStep === 2 && !formData.selectedTutor)
            }
          >
            {t.continue}
          </Button>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}